"use client";

import { LockedCardOverlay } from "@/components/analytics/locked-card-overlay";
import { TierBadge, type Tier } from "@/components/analytics/tier-badge";
import { Area, AreaChart } from "@/components/charts/area-chart";
import { AreaChartLoading } from "@/components/charts/area-chart-loading";
import { Bar } from "@/components/charts/bar";
import { BarChart } from "@/components/charts/bar-chart";
import { BarXAxis } from "@/components/charts/bar-x-axis";
import { Grid } from "@/components/charts/grid";
import { Ring } from "@/components/charts/ring";
import { RingCenter } from "@/components/charts/ring-center";
import { RingChart } from "@/components/charts/ring-chart";
import {
  SankeyChart,
  SankeyLink,
  SankeyNode,
  SankeyTooltip,
} from "@/components/charts/sankey";
import { ChartTooltip } from "@/components/charts/tooltip";
import { XAxis } from "@/components/charts/x-axis";
import { Tooltip } from "@/components/motion/tooltip";
import { paidFeaturesLive } from "@/lib/tier-blocking";
import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import Link from "next/link";
import { useMemo, useState, type ReactNode } from "react";
import { formatNumber } from "./format";

export const ACCENT = "var(--primary)";
export const NEUTRAL = "var(--muted-foreground)";
export const SOFT = "var(--chart-3)";

export function ChartSection({
  title,
  subtitle,
  right,
  children,
  locked,
  tier = "advanced",
  presentationId,
  lockedHeading,
  className,
  dataTour,
}: {
  title: string;
  subtitle?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  locked?: boolean;
  tier?: Tier;
  presentationId?: string | null;
  lockedHeading?: string;
  className?: string;
  dataTour?: string;
}) {
  return (
    <section data-tour={dataTour} className={cn("border-t px-4 py-4", className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-medium">{title}</div>
          {subtitle ? (
            <div className="mt-0.5 text-xs text-muted-foreground">{subtitle}</div>
          ) : null}
        </div>
        {right ?? (locked ? <TierBadge tier={tier} /> : null)}
      </div>
      <div className={cn("relative", locked && "min-h-32")}>
        {children}
        {locked ? (
          <LockedCardOverlay
            bare
            tier={tier}
            presentationId={presentationId}
            heading={lockedHeading}
          />
        ) : null}
      </div>
    </section>
  );
}

const TIER_LABEL: Record<Tier, string> = {
  advanced: "Advanced",
  premium: "Premium",
};

export function lockTierOf(
  response: { locked: boolean; tier_required: string | null } | null | undefined,
): Tier | null {
  if (!response?.locked || !paidFeaturesLive()) return null;
  return response.tier_required === "premium" ? "premium" : "advanced";
}

export function upgradeHrefFor(presentationId?: string | null) {
  return presentationId
    ? `/dashboard/presentations/${presentationId}/settings`
    : "/dashboard/billing";
}

export function StatTile({
  label,
  value,
  hint,
  accent,
  locked,
  lockedPreview = "128",
  presentationId,
  className,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  accent?: boolean;
  locked?: Tier | null;
  lockedPreview?: string;
  presentationId?: string | null;
  className?: string;
}) {
  if (locked) {
    const tierLabel = TIER_LABEL[locked];
    return (
      <Tooltip
        content={`Available on the ${tierLabel} plan`}
        wrapperClassName="flex w-full min-w-0"
      >
        <Link
          href={upgradeHrefFor(presentationId)}
          aria-label={`${label}, locked`}
          className={cn(
            "group block w-full min-w-0 rounded-lg p-3 ring-1 ring-border transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-primary",
            className,
          )}
        >
          <div className="truncate text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 flex h-7 min-w-0 items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                aria-hidden
                className="select-none truncate text-xl font-medium tabular-nums tracking-tight blur-sm"
              >
                {lockedPreview}
              </span>
              <Lock className="size-3.5 shrink-0 text-primary" />
            </span>
            <TierBadge tier={locked} className="shrink-0" />
          </div>
          {hint ? (
            <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
              {hint}
            </div>
          ) : null}
        </Link>
      </Tooltip>
    );
  }

  return (
    <div className={cn("min-w-0 rounded-lg p-3 ring-1 ring-border", className)}>
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-1 truncate text-xl font-medium tabular-nums tracking-tight",
          accent && "text-primary",
        )}
      >
        {value}
      </div>
      {hint ? (
        <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed px-4 py-5 text-center text-xs leading-relaxed text-muted-foreground">
      {children}
    </div>
  );
}

export function ViewsAreaChart({
  data,
  loading,
  showInteractions = true,
}: {
  data: { iso_day: string; views: number; interactions: number }[];
  loading?: boolean;
  showInteractions?: boolean;
}) {
  const chartData = useMemo(
    () =>
      data.map((point) => ({
        date: new Date(point.iso_day),
        views: point.views,
        interactions: point.interactions,
      })),
    [data],
  );
  const series = showInteractions
    ? [
        { key: "views", label: "Views", color: ACCENT },
        { key: "interactions", label: "Taps", color: NEUTRAL },
      ]
    : [{ key: "views", label: "Views", color: ACCENT }];

  return (
    <div className="h-36">
      {loading || chartData.length === 0 ? (
        <AreaChartLoading aspectRatio="auto" className="h-full" />
      ) : (
        <AreaChart
          data={chartData as unknown as Record<string, unknown>[]}
          xDataKey="date"
          margin={{ top: 8, right: 8, bottom: 24, left: 8 }}
          aspectRatio="auto"
          className="h-full"
        >
          <Grid horizontal />
          {series.map((s) => (
            <Area
              key={s.key}
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={1.5}
              fill={s.color}
              fillOpacity={0.2}
              gradientToOpacity={0}
              showHighlight
            />
          ))}
          <XAxis />
          <ChartTooltip
            showDatePill
            rows={(point) =>
              series.map((s) => ({
                color: s.color,
                label: s.label,
                value: formatNumber(Number(point[s.key] ?? 0)),
              }))
            }
          />
        </AreaChart>
      )}
    </div>
  );
}

export function DistributionChart({
  rows,
  valueLabel,
  formatValue = (v) => formatNumber(v),
  height = 112,
}: {
  rows: { label: string; value: number }[];
  valueLabel: string;
  formatValue?: (value: number) => string;
  height?: number;
}) {
  return (
    <div style={{ height }}>
      <BarChart
        data={rows}
        xDataKey="label"
        margin={{ top: 8, right: 4, bottom: 24, left: 4 }}
        aspectRatio="auto"
        className="h-full"
      >
        <Grid horizontal />
        <Bar dataKey="value" fill={ACCENT} lineCap="round" />
        <BarXAxis />
        <ChartTooltip
          indicatorColor={ACCENT}
          rows={(point) => [
            {
              color: ACCENT,
              label: valueLabel,
              value: formatValue(Number(point.value ?? 0)),
            },
          ]}
        />
      </BarChart>
    </div>
  );
}

export function ShareRing({
  items,
  centerLabel,
}: {
  items: { label: string; value: number; color: string; sub?: string }[];
  centerLabel?: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const data = items.map((item) => ({
    label: item.label,
    value: Math.max(0, Math.min(100, Math.round(item.value))),
    maxValue: 100,
    color: item.color,
  }));

  return (
    <div className="flex items-center gap-4">
      <div className="size-28 shrink-0">
        <RingChart
          data={data}
          strokeWidth={9}
          ringGap={4}
          baseInnerRadius={22}
          hoveredIndex={hovered}
          onHoverChange={setHovered}
        >
          {data.map((_, i) => (
            <Ring key={i} index={i} lineCap="round" />
          ))}
          <RingCenter defaultLabel={centerLabel ?? items[0]?.label ?? ""} />
        </RingChart>
      </div>
      <ul className="min-w-0 flex-1 space-y-1">
        {items.map((item, i) => (
          <li
            key={item.label}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            className={cn(
              "flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] transition-opacity",
              hovered !== null && hovered !== i && "opacity-40",
            )}
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: item.color }}
            />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            <span className="tabular-nums text-muted-foreground">
              {item.sub ?? `${Math.round(item.value)} %`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PageFlowChart({
  pageNumber,
  reached,
  continued,
  isLast,
}: {
  pageNumber: number;
  reached: number;
  continued: number;
  isLast: boolean;
}) {
  const left = Math.max(0, reached - continued);
  const nodes = [
    { name: `Page ${pageNumber}`, category: "landing" as const },
    {
      name: isLast ? "Finished" : `Page ${pageNumber + 1}`,
      category: "outcome" as const,
    },
    { name: "Stopped here", category: "source" as const },
  ];
  const links = [
    ...(continued > 0 ? [{ source: 0, target: 1, value: continued }] : []),
    ...(left > 0 ? [{ source: 0, target: 2, value: left }] : []),
  ];
  const color = (node: { category?: string }) =>
    node.category === "source" ? NEUTRAL : ACCENT;

  if (links.length === 0) {
    return <EmptyNote>Viewer flow appears once viewers reach this page.</EmptyNote>;
  }

  return (
    <div className="h-36">
      <SankeyChart
        aspectRatio="auto"
        data={{ nodes, links }}
        margin={{ top: 8, right: 96, bottom: 8, left: 64 }}
        nodeWidth={8}
        nodePadding={16}
      >
        <SankeyLink useGradient strokeOpacity={0.5} getNodeColor={color} />
        <SankeyNode lineCap={3} showLabels getNodeColor={color} />
        <SankeyTooltip formatValue={(v) => `${formatNumber(v)} viewers`} />
      </SankeyChart>
    </div>
  );
}
